/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-organization-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import React, { useEffect, useState } from 'react';

import { useBoolean, useTranslation } from 'hooks';
import { GET_SYSTEM_ORGANISATIONS, SAVE_ORGANISATION_DETAILS, SAVE_ORGANISATION_LOGO } from 'queries/org-admin';
import { GET_ORGANISATION } from 'queries/uam';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import EditLogo from 'views/components/core/edit-logo';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import CustomPhoneInput from 'views/components/core/phone-input';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface EditOrganizationConfDialogInterface {
    open: boolean;
    close(action?: string): any;
    currentOrganization: any;
}

const EditOrganizationDialog = ({ open, close, currentOrganization }: EditOrganizationConfDialogInterface) => {
    const { t } = useTranslation();
    const initialState = {
        type: Constants.ObjectId.Empty,
        recordId: '',
        mobile: '',
        phone: '',
        logo: '',
        created: '',
        updated: '',
        createdBy: '',
        updatedBy: '',
        firstName: '',
        lastName: '',
        email: '',
    };
    const initialError = {
        type: '',
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        phone: '',
    };
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialError });
    const disabledSaveButton = useBoolean(true);
    const [phoneError, setPhoneError] = useState(null);
    const [mobileError, setMobileError] = useState(null);
    const [loadOrganization, { data }] = useLazyQuery(GET_ORGANISATION);
    const [saveOrganisationDetails] = useMutation(SAVE_ORGANISATION_DETAILS);
    const [saveOrganisationLogo] = useMutation(SAVE_ORGANISATION_LOGO);

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const emailReg =
            /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*;{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/;
        let error = validateFields(value, field, length, required);

        if (field === 'email' && value && !emailReg.test(value.toLowerCase()))
            error = { email: t('uam:incorrectEmailFormat') };

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (fieldName: string, length?: number, required?: boolean) => (event: any) => {
        const { value } = event.target;
        if (disabledSaveButton.value) disabledSaveButton.setFalse();
        setState({ ...state, [fieldName]: value });

        validate(value, fieldName, length, required);
    };

    useEffect(() => {
        if (currentOrganization) loadOrganization({ variables: { key: currentOrganization.key } });
        else setState({ ...initialState });
    }, [currentOrganization]);

    useEffect(() => {
        if (data?.organisation) {
            const fullOrganisation = data.organisation;
            setState({
                type: fullOrganisation.type,
                recordId: fullOrganisation.recordId,
                logo: fullOrganisation.logo,
                created: fullOrganisation.created,
                updated: fullOrganisation.updated,
                createdBy: fullOrganisation.createdBy,
                updatedBy: fullOrganisation.updatedBy,
                firstName: fullOrganisation.primaryContact.firstName || '',
                lastName: fullOrganisation.primaryContact.lastName || '',
                email: fullOrganisation.primaryContact.email || '',
                mobile: fullOrganisation.primaryContact.mobile || '',
                phone: fullOrganisation.primaryContact.phone || '',
            });
        }
    }, [data]);

    const onClose = (action?: string) => {
        disabledSaveButton.setTrue();
        setState({ ...initialState });
        setErrors({ ...initialError });
        close(action);
    };

    const checkRequiredFields = (fields: string[]) => {
        let isValid = true;
        let newErrors = { ...errors };
        fields.forEach((field) => {
            let error;
            if (field === 'type')
                error = {
                    type: state[field] === 'NONE' ? t('ra:requiredFieldError') : null,
                };
            else error = validateFields((state as any)[field], field, 0, true);
            newErrors = {
                ...newErrors,
                ...error,
            };
        });
        if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) {
            isValid = false;
        }
        setErrors({ ...newErrors });
        return isValid;
    };

    const checkForErrors = () => Object.keys(errors).filter((item) => (errors as any)[item]).length > 0;

    const successRequest = () => {
        NotificationService.showNotification(t('admin:organizationSuccessfulUpdated', { id: state.recordId }));
        onClose('add');
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const validationOnSubmit = () => {
        const isErrors = checkForErrors();
        const isRequiredFieldsError = checkRequiredFields(['firstName', 'lastName', 'email', 'phone', 'type']);
        if (!isErrors && !isRequiredFieldsError) return false;

        if (phoneError || mobileError) {
            setErrors({
                ...errors,
                mobile: mobileError,
                phone: phoneError,
            });
            return false;
        }
        return true;
    };

    const submit = () => {
        if (validationOnSubmit()) {
            disabledSaveButton.setTrue();
            saveOrganisationDetails({
                variables: {
                    organisationKey: currentOrganization.key,
                    type: state.type,
                    primaryContact: {
                        email: state.email,
                        firstName: state.firstName,
                        lastName: state.lastName,
                        mobile: state.mobile,
                        phone: state.phone,
                    },
                    reason: '',
                },
            })
                .then(() => {
                    const logoChanged = (state.logo ?? '') !== data?.organisation?.logo;
                    if (logoChanged) {
                        saveOrganisationLogo({
                            variables: {
                                organisationKey: currentOrganization.key,
                                logo: state.logo?.substring(state.logo.lastIndexOf(',') + 1) || '',
                            },
                            refetchQueries: [
                                { query: GET_SYSTEM_ORGANISATIONS },
                                {
                                    query: GET_ORGANISATION,
                                    variables: {
                                        key: currentOrganization.key,
                                    },
                                },
                            ],
                        })
                            .then(() => {
                                successRequest();
                            })
                            .catch(() => {
                                errorRequest();
                            });
                    } else {
                        successRequest();
                    }
                })
                .catch(() => {
                    errorRequest();
                });
        }
    };

    const showModalUpdatedInfo = (): boolean =>
        !!state.created || !!state.createdBy || !!state.updated || !!state.updatedBy;
    const typesOptions = Constants.OrganisationTypes.map((type) => ({
        id: type,
        text: t(`admin:${type}`),
        default: false,
    }));
    typesOptions.unshift({ id: 'NONE', text: t('ra:selectType'), default: true });

    const onLogoChange = (base64Src: string) => {
        if (disabledSaveButton.value) disabledSaveButton.setFalse();

        setState({ ...state, logo: base64Src });
    };

    return (
        <>
            {open && (
                <CustomDialog
                    onClose={() => {
                        onClose();
                    }}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    open={open && data?.organisation}
                >
                    <DialogTitle id="edit-organization-modal-title">
                        {t('admin:editOrganizationNumber', { id: state?.recordId })}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="edit-organization-dialog-content">
                        <div className="value-list">
                            <p>
                                <span>{`${t('admin:organization')}: `}</span>
                                {currentOrganization?.name}
                            </p>
                        </div>
                        <div className="type-edc-wrapper" style={{ marginBottom: 33 }}>
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <SelectWithCategories
                                    showWarning={false}
                                    customClassName="form-action-type type"
                                    fieldSelect="type"
                                    onChange={onChange}
                                    label={`${t('type')}*`}
                                    filterable={false}
                                    searchable={false}
                                    labelField="type"
                                    dataArray={typesOptions}
                                    customId="select-outlined-type"
                                    currentState={state.type}
                                    error={errors.type}
                                />
                            </FormControl>
                        </div>
                        <OutlinedWrapper customClass="mb" label={t('admin:primaryContact')}>
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type not-full mr">
                                    <TextField
                                        label={`${t('firstName')}*`}
                                        id="edit-organization-dialog-firstName"
                                        fullWidth
                                        value={state.firstName}
                                        onChange={onChange('firstName', 50)}
                                        placeholder={t('admin:enterFirstName')}
                                        helperText={
                                            <CharactersRemaining
                                                id="edit-organization-dialog-firstName-help-text"
                                                length={state.firstName?.length ?? 0}
                                                max={50}
                                                error={errors.firstName}
                                            />
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        className="before-select mb"
                                        error={!!errors.firstName}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type not-full">
                                    <TextField
                                        label={`${t('lastName')}*`}
                                        id="edit-organization-dialog-path"
                                        fullWidth
                                        value={state.lastName}
                                        onChange={onChange('lastName', 50)}
                                        placeholder={t('admin:enterLastName')}
                                        helperText={
                                            <CharactersRemaining
                                                id="edit-organization-dialog-lastName-help-text"
                                                length={state.lastName?.length ?? 0}
                                                max={50}
                                                error={errors.lastName}
                                            />
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        className="before-select mb"
                                        error={!!errors.lastName}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type">
                                    <TextField
                                        label={`${t('email')}*`}
                                        id="edit-organization-dialog-email"
                                        fullWidth
                                        value={state.email}
                                        onChange={onChange('email', 320)}
                                        placeholder={t('enterYourEmail')}
                                        helperText={
                                            <CharactersRemaining
                                                id="edit-organization-dialog-email-help-text"
                                                length={state.email?.length ?? 0}
                                                max={320}
                                                error={errors.email}
                                            />
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        className="before-select mb"
                                        error={!!errors.email}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper" style={{ marginBottom: 23 }}>
                                <FormControl variant="outlined" className="form-control-type not-full mr">
                                    <CustomPhoneInput
                                        customId="edit-organization-dialog-mobile"
                                        phone={state.mobile || ''}
                                        customClassName={errors.mobile ? 'error' : ''}
                                        error={errors.mobile}
                                        isValid={(value: string | null) => {
                                            setMobileError(value);
                                        }}
                                        label={t('uam:phone')}
                                        onChange={(event: string | null | undefined) => {
                                            const newEvent = { target: { value: event } };
                                            onChange('mobile')(newEvent);
                                        }}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type not-full">
                                    <CustomPhoneInput
                                        customId="account-phone"
                                        phone={state.phone || ''}
                                        customClassName={errors.phone ? 'error' : ''}
                                        error={errors.phone}
                                        label={`${t('admin:phoneNumber')}*`}
                                        isValid={(value: string | null) => {
                                            setPhoneError(value);
                                        }}
                                        onChange={(event: string | null | undefined) => {
                                            const newEvent = { target: { value: event } };
                                            onChange('phone')(newEvent);
                                        }}
                                        required
                                    />
                                </FormControl>
                            </div>
                        </OutlinedWrapper>
                        <EditLogo logo={state.logo} onLogoChange={onLogoChange} />
                        <DialogActions
                            style={{ paddingBottom: 25, paddingTop: 8 }}
                            className={`actions-info-wrapper-edit-cv ${!showModalUpdatedInfo() ? 'flex-end' : ''}`}
                        >
                            <ModalUpdatedInfo item={state} />
                            <Button
                                disabled={
                                    disabledSaveButton.value ||
                                    Object.keys(errors).filter((item) => (errors as any)[item]).length > 0
                                }
                                className="edit-organization-btn"
                                text="update"
                                onClick={submit}
                            />
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
        </>
    );
};

export default EditOrganizationDialog;
